/**
 * Customer record, rebuilt around what a sales or credit admin comes here to decide: is this account
 * verified and allowed to order, what terms and credit does it hold, and is its record right.
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/customer-profile/user-info.tsx
 *   the identity header over a figure strip, and the company-users list (non-ecommerce, reason in
 *   screen-map §6 — ecommerce has no customer profile);
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-view.tsx
 *   the 8/4 main-and-side grid, decisions in the main column and reference cards at the side;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/stats.tsx
 *   the solid colour icon tiles on the four credit figures;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx
 *   icon tile + label/value blocks in the registered-details card.
 *
 * Header, card titles, info blocks and the copy button are `components/detail-parts.tsx`'s. The
 * screen-to-template mapping is specs/017-isomorphic-theme-only/contracts/screen-map.md.
 *
 * Rules this screen keeps:
 * - Every figure is the API's (constitution I). Over-limit is `summary.overLimit`, never a compare here.
 * - The receipts caveat sits above the credit figures whenever `excludesReceipts` — structurally, as
 *   `CaveatBanner`'s child.
 * - Every field carries its `FieldOwnerBadge` (§14.4, `010` FR-016): Tally owns legal name, GSTIN and
 *   billing address; the portal owns tier, shipping address and company users.
 * - Status moves are compared by `customerStatusLabel`, which reads both the numeric enum and the
 *   string the live API actually sends — a bare `status === 1` never matched `"active"` on the wire.
 */
import { useState, useEffect, type ReactNode } from "react";
import toast from "react-hot-toast";
import { Link, useLoaderData, useOutletContext, useRevalidator, type LoaderFunctionArgs } from "react-router-dom";
import { Alert, Avatar, Badge, Button, Dropdown, Text } from "rizzui";
import {
  PiBuildingsDuotone as CompanyIcon,
  PiCheckBold as Check,
  PiCreditCardDuotone as CreditCard,
  PiDotsThreeOutlineVerticalFill as MoreIcon,
  PiEnvelopeSimpleDuotone as MailIcon,
  PiHandshakeDuotone as HandshakeIcon,
  PiHourglassDuotone as Hourglass,
  PiIdentificationCardDuotone as IdIcon,
  PiLinkSimpleDuotone as LinkIcon,
  PiMapPinDuotone as MapPinIcon,
  PiPauseCircleDuotone as SuspendIcon,
  PiPencilSimpleLineDuotone as EditIcon,
  PiProhibitDuotone as CloseIcon,
  PiReceiptDuotone as Receipt,
  PiTrendDownDuotone as DemoteIcon,
  PiUserDuotone as PersonIcon,
  PiUsersThreeDuotone as UsersIcon,
  PiWalletDuotone as Wallet,
  PiWarningDuotone as WarningIcon,
} from "react-icons/pi";
import { CaveatBanner, WidgetCard, ErrorSurface, Field, MetricCard, FieldOwnerBadge, cn, getStatusBadge, problemTitle } from "@steelyard/theme";
import { SelectField } from "../../components/select-field";
import { ADDRESS_STATES, stateName } from "../../lib/invoice-model";
import { AssignTermsForm, type TierOption } from "../../components/customers/assign-terms-form";
import { CustomerOverrideForm } from "../../components/customers/customer-override-form";
import { DemoteCustomerForm, DemotionRefused } from "../../components/customers/customer-demote-form";
import { CustomerTallyLink } from "../../components/customers/customer-tally-link";
import { useConfirm } from "../../components/confirm-modal";
import { CardTitle, CopyButton, InfoBlock, PageHero, StatusPill, iconMenuTriggerClass } from "../../components/detail-parts";
import { listActiveTiers } from "../../lib/services/tier-options";
import {
  addressTypeLabel,
  approveCustomer,
  assignTerms,
  closeCustomer,
  customerByKey,
  customerStatusLabel,
  demoteCustomer,
  overrideCustomer,
  resendCustomerInvitation,
  setBillingAddress,
  suspendCustomer,
  reinstateCustomer,
  type CustomerDetail,
  type DemotionRefusal,
} from "../../lib/services/customers";
import { can } from "../../lib/permissions";
import { formatMoment, formatMoney, roleName } from "../../lib/format";
import type { AccountSummary, PermissionState } from "../../types";

export async function detailLoader({ params, request }: LoaderFunctionArgs) {
  const customer = await customerByKey(request, params.customerNumber!);
  return { id: customer.summary.customerId, customer };
}

export default function CustomersDetail() {
  const { id, customer } = useLoaderData() as Awaited<ReturnType<typeof detailLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const revalidator = useRevalidator();
  const confirm = useConfirm();

  const [tiers, setTiers] = useState<TierOption[] | null>(null);
  const [tiersError, setTiersError] = useState<unknown>(null);
  const [banner, setBanner] = useState<ReactNode>(null);
  const [demoteOpen, setDemoteOpen] = useState(false);
  const [demotionRefused, setDemotionRefused] = useState<DemotionRefusal | null>(null);
  const [formError, setFormError] = useState<unknown>(null);
  const [demoteError, setDemoteError] = useState<unknown>(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [billingError, setBillingError] = useState<unknown>(null);
  const [billingBusy, setBillingBusy] = useState(false);

  const mayApprove = can(permissionState, "customers.terms");
  const mayDemote = can(permissionState, "customers.administer");
  // Approve/suspend/close and the PATCH override are `[Authorize("customers.administer")]`; assigning terms
  // (`POST .../terms`) is `customers.terms`, which Accounts holds too (spec §5).
  const mayWrite = can(permissionState, "customers.administer");

  useEffect(() => {
    if (!mayApprove && !mayDemote) return;
    listActiveTiers().then(setTiers).catch(setTiersError);
  }, [mayApprove, mayDemote]);

  if (!customer) return <EmptyStateNoCustomer />;

  const defaultTierId = tiers?.find((tier) => tier.isDefault)?.id;
  const tierName = customer.summary.tierName ?? tiers?.find((tier) => tier.id === customer.summary.tierId)?.name;
  const { summary } = customer;
  const status = customerStatusLabel(summary.status);
  const outstanding = formatMoney(Number(summary.outstanding));
  const canAssignTerms = mayApprove && tiers !== null && tiers.length > 0;
  // A company that signed up with its legal name only (owner decision 2026-10-19). It can be approved as it
  // is — the customer gives its billing address at its first order — but staff may add it here first.
  const needsBilling =
    status === "pending_verification" && !customer.addresses.some((address) => addressTypeLabel(address.type) === "billing");

  async function handleBilling(formData: FormData) {
    setBillingError(null);
    setBillingBusy(true);
    try {
      await setBillingAddress(id, formData);
      toast.success("Billing address saved");
      await revalidator.revalidate();
    } catch (err) {
      setBillingError(err);
      toast.error("Could not save the billing address");
    } finally {
      setBillingBusy(false);
    }
  }

  async function handleAssign(formData: FormData) {
    setFormError(null);
    try {
      await assignTerms(id, formData);
      setBanner(
        <div role="status">
          <Alert color="success" variant="flat" bar>
            Tier, credit limit and payment term are set. This customer is now B2B: their portal shows unit rates
            instead of piece prices from their next load.
          </Alert>
        </div>,
      );
      toast.success("Terms assigned");
      revalidator.revalidate();
    } catch (err) {
      setFormError(err);
      toast.error("Could not assign terms");
    }
  }

  async function handleOverride(formData: FormData) {
    setFormError(null);
    try {
      await overrideCustomer(id, formData);
      setBanner(
        <div role="status">
          <Alert color="success" variant="flat" bar>
            Saved as a local override, with the reason recorded.
          </Alert>
        </div>,
      );
      toast.success("Override saved");
      revalidator.revalidate();
    } catch (err) {
      setFormError(err);
      toast.error("Could not save the override");
    }
  }

  async function handleDemote(formData: FormData) {
    setDemoteError(null);
    try {
      const { refused } = await demoteCustomer(id, formData);
      if (refused) {
        setDemotionRefused(refused);
        setDemoteOpen(false);
        toast.error(refused.title ?? "Demotion refused");
        return;
      }
      setDemoteOpen(false);
      setBanner(
        <div role="status">
          <Alert color="success" variant="flat" bar>
            Demoted to the Default Tier. Piece prices and payment before dispatch from their next load. The reason is
            in the audit log.
          </Alert>
        </div>,
      );
      toast.success("Customer demoted");
      revalidator.revalidate();
    } catch (err) {
      setDemoteError(err);
      toast.error("Could not demote this customer");
    }
  }

  // Suspend and Close confirm in-page and record a typed reason (ChangeCustomerStatusHandler's
  // optional reason, written to the audit entry); Approve is a single click, matching
  // OrderStateMenu's un-confirmed "Approve" item.
  async function runStatusChange(action: () => Promise<CustomerDetail>, done: string) {
    setStatusBusy(true);
    try {
      await action();
      toast.success(`${summary.displayName}: ${done}`);
      // A toast alone is easy to miss mid-click — an on-page banner (the same pattern
      // handleAssign/handleOverride/handleDemote already use) gives an admin unambiguous
      // confirmation without relying on the loader revalidation's re-render timing.
      setBanner(
        <div role="status">
          <Alert color="success" variant="flat" bar>
            {summary.displayName}: {done}.
          </Alert>
        </div>,
      );
      await revalidator.revalidate();
    } catch (err) {
      toast.error(problemTitle(err, `${summary.displayName}: Infra Junction refused that change`));
    } finally {
      setStatusBusy(false);
    }
  }

  function handleSuspend() {
    confirm({
      title: `Suspend ${summary.displayName}`,
      consequence: "They will not be able to order until reinstated.",
      confirmLabel: "Suspend",
      reason: "Why is this account being suspended?",
      onConfirm: (reason) => void runStatusChange(() => suspendCustomer(id, reason), "suspended"),
    });
  }

  function handleReinstate() {
    confirm({
      title: `Reinstate ${summary.displayName}`,
      consequence: "The suspension is lifted and they can order again. Their tier, credit limit and terms are unchanged.",
      confirmLabel: "Reinstate",
      reason: "Why is the suspension being lifted?",
      onConfirm: (reason) => void runStatusChange(() => reinstateCustomer(id, reason), "reinstated"),
    });
  }

  function handleClose() {
    confirm({
      title: `Close ${summary.displayName}'s account`,
      consequence: "This is terminal — there is no reopen route.",
      confirmLabel: "Close account",
      reason: "Why is this account being closed?",
      onConfirm: (reason) => void runStatusChange(() => closeCustomer(id, reason), "closed"),
    });
  }

  return (
    <div className="@container flex flex-col gap-6">
      {/* ── Hero: who this is, where the account stands, and the status decision ─────────────── */}
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Customers", href: "/customers" }, { name: summary.displayName }]}
        back={{ href: "/customers", label: "All customers" }}
        icon={summary.type === "company" ? <CompanyIcon aria-hidden className="size-9" /> : <PersonIcon aria-hidden className="size-9" />}
        title={summary.displayName}
        // Only when it adds something: legalName usually equals displayName, and the same name printed
        // twice with no label read as a mistake. The labelled value lives in "Registered details".
        description={customer.legalName !== summary.displayName ? `Legal name: ${customer.legalName}` : undefined}
        meta={
          <>
            <StatusPill status={status} />
            <Badge variant="flat" color={summary.isDefaultTier ? "info" : "secondary"} rounded="pill" className="gap-1.5">
              <Wallet aria-hidden className="size-4" />
              {!summary.isDefaultTier
                ? `B2B · ${tierName ?? "Assigned tier"}`
                : summary.type === "company"
                  ? "B2B · Awaiting trade tier"
                  : "B2C · Default Tier"}
            </Badge>
            <Badge variant="flat" rounded="pill" className={cn("gap-1.5", summary.tallyLedgerLinked ? "bg-green-lighter text-green-dark" : "bg-gray-100 text-gray-600")}>
              <LinkIcon aria-hidden className="size-4" />
              {summary.tallyLedgerLinked ? "Tally ledger linked" : "No Tally ledger yet"}
            </Badge>
            {summary.overLimit ? (
              <Badge variant="flat" color="danger" rounded="pill" className="gap-1.5">
                <WarningIcon aria-hidden className="size-4" />
                Over credit limit
              </Badge>
            ) : null}
            {customer.divergesFromAccounting ? (
              <Badge variant="flat" color="warning" rounded="pill">
                Diverges from Tally
              </Badge>
            ) : null}
          </>
        }
        actions={
          <>
            {canAssignTerms ? (
              <a href="#trade-terms">
                <Button as="span" variant="outline">
                  <HandshakeIcon aria-hidden className="me-1.5 size-4 text-secondary" />
                  {summary.isDefaultTier ? "Assign terms" : "Change terms"}
                </Button>
              </a>
            ) : null}
            {mayWrite && status === "pending_verification" ? (
              <Button
                type="button"
                isLoading={statusBusy}
                onClick={() => void runStatusChange(() => approveCustomer(id), "approved")}
              >
                <Check aria-hidden className="me-1.5 size-4" />
                Approve customer
              </Button>
            ) : null}
            {mayWrite && status === "suspended" ? (
              <Button type="button" isLoading={statusBusy} onClick={handleReinstate}>
                <Check aria-hidden className="me-1.5 size-4" />
                Reinstate…
              </Button>
            ) : null}
            {/* H-1: promoted out of the "More actions" menu — this is the only way a newly-approved
                account ever gets a working sign-in, so it needs to be visible the moment status
                flips to active, not discovered by digging into a dropdown. Idempotent either way
                (rewrites the token and resends; a no-op if they already have a password). */}
            {mayWrite && status === "active" ? (
              <Button
                type="button"
                variant="outline"
                isLoading={statusBusy}
                title="Has no effect if they already have a password."
                onClick={() =>
                  void runStatusChange(() => resendCustomerInvitation(id).then(() => customer), "sign-in invitation sent")
                }
              >
                <MailIcon aria-hidden className="me-1.5 size-4" />
                Send sign-in invitation
              </Button>
            ) : null}
            {mayWrite && status !== "closed" ? (
              <Dropdown placement="bottom-end">
                <Dropdown.Trigger disabled={statusBusy} aria-label={`More actions for ${summary.displayName}`} className={iconMenuTriggerClass}>
                  <MoreIcon aria-hidden className="size-4" />
                </Dropdown.Trigger>
                <Dropdown.Menu className="w-56">
                  {status === "active" ? (
                    <Dropdown.Item onClick={handleSuspend} className="gap-2.5 text-sm text-orange-dark">
                      <SuspendIcon aria-hidden className="size-4 shrink-0" />
                      Suspend…
                    </Dropdown.Item>
                  ) : null}
                  <Dropdown.Item onClick={handleClose} className="gap-2.5 text-sm text-red-dark">
                    <CloseIcon aria-hidden className="size-4 shrink-0" />
                    Close account…
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : null}
          </>
        }
      />

      {banner}
      {demotionRefused ? <DemotionRefused refusal={demotionRefused} /> : null}
      <StatusNotice status={status} mayWrite={mayWrite} needsBilling={needsBilling} />

      {/* ── Credit health — the API's own figures, under the receipts caveat when it applies ─── */}
      <Figures excludesReceipts={Boolean(summary.excludesReceipts)} mayOpenTally={can(permissionState, "integrations.read")}>
        <MetricCard
          title="Credit limit"
          metric={summary.creditLimit === null || summary.creditLimit === undefined ? "None" : formatMoney(Number(summary.creditLimit))}
          icon={<Wallet aria-hidden className="size-6" />}
          iconClassName="bg-secondary text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">{summary.isDefaultTier ? "Default Tier — pays before dispatch" : "Granted with the tier"}</Text>}
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Available credit"
          metric={customer.availableCredit === null || customer.availableCredit === undefined ? "None" : formatMoney(Number(customer.availableCredit))}
          icon={<CreditCard aria-hidden className="size-6" />}
          iconClassName="bg-green text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">What they can still order on credit</Text>}
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Outstanding"
          metric={outstanding}
          icon={<Receipt aria-hidden className="size-6" />}
          iconClassName={cn("text-white shadow-sm", summary.overLimit ? "bg-red" : "bg-blue")}
          info={
            summary.overLimit ? (
              <Badge variant="flat" color="danger" size="sm" rounded="pill" className="mt-1">
                Over the credit limit
              </Badge>
            ) : (
              <Text className="mt-0.5 text-xs text-gray-500">
                {summary.receiptsLastAppliedAt ? `Receipts applied ${formatMoment(summary.receiptsLastAppliedAt)}` : "No receipts applied yet"}
              </Text>
            )
          }
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Payment term"
          metric={customer.paymentTermDays === null || customer.paymentTermDays === undefined ? "None" : `${customer.paymentTermDays} days`}
          icon={<Hourglass aria-hidden className="size-6" />}
          iconClassName="bg-primary text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">From invoice to due date</Text>}
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
      </Figures>

      <div className="grid grid-cols-1 items-start gap-6 @5xl:grid-cols-12">
        {/* ── Decisions ──────────────────────────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-6 @5xl:col-span-8">
          <section id="trade-terms" className="scroll-mt-24">
            <WidgetCard
              rounded="xl"
              headerClassName="flex flex-wrap items-center justify-between gap-3"
              title={
                <CardTitle icon={<HandshakeIcon aria-hidden className="size-5" />} tile="bg-secondary-lighter text-secondary-dark">
                  Trade terms
                </CardTitle>
              }
              action={
                <span className="flex items-center gap-2 text-sm text-gray-500">
                  Request
                  {customer.tradeTermsRequestStatus ? (
                    getStatusBadge(customer.tradeTermsRequestStatus)
                  ) : (
                    <Badge variant="flat" size="sm" rounded="pill" className="bg-gray-100 text-gray-600">
                      None outstanding
                    </Badge>
                  )}
                </span>
              }
            >
              <div className="mt-5 flex flex-col gap-5">
                <div className="flex flex-wrap items-center gap-2 rounded-lg bg-secondary-lighter/30 px-4 py-3 text-sm text-gray-700 dark:bg-secondary-lighter/10">
                  <Text as="span">
                    {summary.isDefaultTier
                      ? "On the Default Tier — piece prices, paid before dispatch. Assigning a tier, credit limit and payment term, all in one act, converts them to B2B."
                      : "On an assigned tier — unit rates, invoiced against a credit limit."}
                  </Text>
                  <FieldOwnerBadge owner="portal" />
                </div>

                {mayApprove && tiersError ? <ErrorSurface error={tiersError} action="load the available tiers" /> : null}

                {mayApprove && tiers && tiers.length > 0 ? (
                  <div className="rounded-xl border border-muted p-5">
                    <Text className="mb-4 font-semibold text-gray-900">{summary.isDefaultTier ? "Assign trade terms" : "Change tier, limit or term"}</Text>
                    <AssignTermsForm onSubmit={handleAssign} tiers={tiers} customerName={summary.displayName} tradeTermsRequestId={customer.pendingTradeTermsRequestId ?? undefined} currentOutstanding={outstanding} current={summary.isDefaultTier ? undefined : { tierId: summary.tierId, creditLimit: summary.creditLimit, paymentTermDays: customer.paymentTermDays }} error={formError} />
                  </div>
                ) : null}

                {mayDemote && !summary.isDefaultTier ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red/30 bg-red-lighter/30 p-4 dark:bg-red-lighter/10">
                      <div className="flex items-start gap-3">
                        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-red-lighter text-red-dark">
                          <DemoteIcon aria-hidden className="size-5" />
                        </span>
                        <div>
                          <Text className="font-semibold text-red-dark">Demote to the Default Tier</Text>
                          <Text className="mt-0.5 text-sm text-gray-600">
                            Removes their tier, credit limit and payment term. Refused while a balance is outstanding, and always audit-logged with a reason.
                          </Text>
                        </div>
                      </div>
                      <Button
                        color="danger"
                        variant="outline"
                        onClick={() => {
                          setDemoteError(null);
                          setDemoteOpen(true);
                        }}
                      >
                        Demote…
                      </Button>
                    </div>
                    {demoteOpen ? (
                      defaultTierId ? (
                        <DemoteCustomerForm
                          open={demoteOpen}
                          onSubmit={handleDemote}
                          customerName={summary.displayName}
                          defaultTierId={defaultTierId}
                          outstanding={outstanding}
                          error={demoteError}
                          cancel={
                            <Button type="button" variant="outline" onClick={() => setDemoteOpen(false)}>
                              Cancel
                            </Button>
                          }
                        />
                      ) : (
                        <ErrorSurface
                          error={{
                            title: "Catalog reports no default tier",
                            detail:
                              "A demotion names the tier Catalog marks as default, and Catalog returned none. Demoting without it would move this customer to a tier nobody chose.",
                          }}
                          action="demote this customer"
                        />
                      )
                    ) : null}
                  </div>
                ) : null}
              </div>
            </WidgetCard>
          </section>

          {mayWrite && needsBilling ? (
            <WidgetCard
              rounded="xl"
              headerClassName="items-center"
              title={
                <CardTitle icon={<Receipt aria-hidden className="size-5" />} tile="bg-orange-lighter text-orange-dark">
                  Billing address
                </CardTitle>
              }
              description={
                <Text className="mt-1 text-sm text-gray-500">
                  Optional: the customer is asked for it at its first order. Add it here if you already have it —
                  invoices take the GST place of supply from its state. The GSTIN goes in &ldquo;Correct this
                  record&rdquo; below.
                </Text>
              }
            >
              <form
                className="mt-5 grid gap-4 @md:grid-cols-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  void handleBilling(new FormData(event.currentTarget));
                }}
              >
                {billingError ? (
                  <div className="@md:col-span-2">
                    <ErrorSurface error={billingError} action="save the billing address" />
                  </div>
                ) : null}
                <Field label="Address line 1" name="line1" required maxLength={200} />
                <Field label="Address line 2" name="line2" maxLength={200} />
                <Field label="City" name="city" required maxLength={100} />
                <Field label="Pincode" name="pincode" required inputMode="numeric" maxLength={6} />
                <SelectField label="State" name="stateCode" required defaultValue="">
                  <option value="">Select state</option>
                  {ADDRESS_STATES.map((state) => (
                    <option key={state.code} value={state.code}>
                      {state.name}
                    </option>
                  ))}
                </SelectField>
                <div className="flex items-end justify-end">
                  <Button type="submit" isLoading={billingBusy}>
                    Save billing address
                  </Button>
                </div>
              </form>
            </WidgetCard>
          ) : null}

          {mayWrite ? (
            <WidgetCard
              rounded="xl"
              headerClassName="items-center"
              title={
                <CardTitle icon={<EditIcon aria-hidden className="size-5" />} tile="bg-secondary-lighter text-secondary-dark">
                  Correct this record
                </CardTitle>
              }
              description={<Text className="mt-1 text-sm text-gray-500">Check which system owns a field before you change it.</Text>}
              action={
                customer.divergesFromAccounting ? (
                  <Badge variant="flat" color="warning" rounded="pill">
                    Diverges from Tally
                  </Badge>
                ) : null
              }
            >
              <div className="mt-5">
                <CustomerOverrideForm onSubmit={handleOverride} customer={customer} error={formError} />
              </div>
            </WidgetCard>
          ) : null}
        </div>

        {/* ── Reference ──────────────────────────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-6 @5xl:col-span-4">
          <WidgetCard
            rounded="xl"
            headerClassName="items-center"
            title={
              <CardTitle icon={<IdIcon aria-hidden className="size-5" />} tile="bg-blue-lighter text-blue-dark">
                Registered details
              </CardTitle>
            }
          >
            <div className="mt-5 flex flex-col gap-4">
              <InfoBlock icon={<CompanyIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="Legal name">
                <Text className="font-medium text-gray-900">{customer.legalName}</Text>
                <FieldOwnerBadge owner="tally" className="mt-1 w-fit" />
              </InfoBlock>
              <InfoBlock icon={<IdIcon aria-hidden className="size-4" />} tile="bg-secondary-lighter text-secondary-dark" label="GSTIN">
                {customer.gstin ? (
                  <span className="flex items-center gap-1">
                    <Text as="span" className="font-mono text-sm text-gray-900">
                      {customer.gstin}
                    </Text>
                    <CopyButton value={customer.gstin} label="Copy GSTIN" />
                  </span>
                ) : (
                  <Text className="text-sm text-gray-500">Not given</Text>
                )}
                <FieldOwnerBadge owner="tally" className="mt-1 w-fit" />
              </InfoBlock>

              <div className="border-t border-dashed border-muted" />

              {customer.addresses.length === 0 ? <Text className="text-sm text-gray-500">No addresses on record.</Text> : null}
              {customer.addresses.map((address, index) => {
                const billing = addressTypeLabel(address.type) === "billing";
                return (
                  <InfoBlock
                    key={address.id ?? `${address.type}-${index}`}
                    icon={billing ? <Receipt aria-hidden className="size-4" /> : <MapPinIcon aria-hidden className="size-4" />}
                    tile={billing ? "bg-orange-lighter text-orange-dark" : "bg-green-lighter text-green-dark"}
                    label={billing ? "Billing address" : "Shipping address"}
                    action={
                      address.isDefault ? (
                        <Badge variant="flat" size="sm" rounded="pill" className="bg-gray-100 text-gray-600">
                          Default
                        </Badge>
                      ) : null
                    }
                  >
                    <Text className="text-sm leading-relaxed text-gray-900">
                      {[address.line1, address.line2, address.city, stateName(address.stateCode), address.pincode].filter(Boolean).join(", ")}
                    </Text>
                    <FieldOwnerBadge owner={billing ? "tally" : "portal"} className="mt-1 w-fit" />
                  </InfoBlock>
                );
              })}
            </div>
          </WidgetCard>

          {customer.users.length > 0 ? (
            <WidgetCard
              rounded="xl"
              headerClassName="items-center"
              title={
                <CardTitle icon={<UsersIcon aria-hidden className="size-5" />} tile="bg-green-lighter text-green-dark">
                  <span className="flex items-center gap-2">
                    Company users
                    <Badge variant="flat" color="success" size="sm" rounded="pill">
                      {customer.users.length}
                    </Badge>
                  </span>
                </CardTitle>
              }
            >
              <FieldOwnerBadge owner="portal" className="mt-3 w-fit" />
              <ul className="mt-4 flex flex-col gap-1">
                {customer.users.map((user) => (
                  <li key={user.id} className={cn("flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-gray-50 dark:hover:bg-gray-100/40", !user.isActive && "opacity-60")}>
                    <Avatar name={user.name} size="sm" color="secondary" />
                    <div className="min-w-0 flex-1">
                      <Text className="truncate text-sm font-medium text-gray-900">{user.name}</Text>
                      <a href={`mailto:${user.email}`} className="block truncate text-xs text-gray-500 hover:text-secondary hover:underline">
                        {user.email}
                      </a>
                      {user.orderValueLimit !== null && user.orderValueLimit !== undefined ? (
                        <Text className="text-xs text-gray-500">Orders up to {formatMoney(Number(user.orderValueLimit))}</Text>
                      ) : null}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {typeof user.role === "string" ? (
                        <Badge variant="outline" size="sm" rounded="pill" className="whitespace-nowrap border-muted text-gray-600">
                          {roleName(user.role)}
                        </Badge>
                      ) : null}
                      {user.isActive ? null : (
                        <Badge variant="flat" color="danger" size="sm" rounded="pill">
                          Deactivated
                        </Badge>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </WidgetCard>
          ) : null}

          <WidgetCard
            rounded="xl"
            headerClassName="items-center"
            title={
              <CardTitle icon={<LinkIcon aria-hidden className="size-5" />} tile="bg-orange-lighter text-orange-dark">
                Tally ledger
              </CardTitle>
            }
          >
            <div className="mt-5">
              <CustomerTallyLink linked={summary.tallyLedgerLinked} divergesFromAccounting={customer.divergesFromAccounting} />
            </div>
          </WidgetCard>
        </div>
      </div>
    </div>
  );
}

/** Where the account stands, in words, for the three statuses that stop or limit ordering. */
function StatusNotice({ status, mayWrite, needsBilling }: { status: string; mayWrite: boolean; needsBilling: boolean }) {
  if (status === "pending_verification") {
    return (
      <Alert color="info" variant="flat" bar icon={<IdIcon aria-hidden className="size-5" />}>
        <Text className="font-semibold text-blue-dark">Awaiting verification</Text>
        <Text className="mt-0.5 text-sm">
          {needsBilling
            ? `This company signed up with its legal name only — no GSTIN or billing address yet. ${mayWrite ? "You can approve it from the header as it is: it gives its billing address at its first order (or add it below)." : "It gives its billing address at its first order."}`
            : `Check the registered details and GSTIN below${mayWrite ? ", then approve the account from the header" : ""}.`}
        </Text>
      </Alert>
    );
  }
  if (status === "suspended") {
    return (
      <Alert color="warning" variant="flat" bar icon={<SuspendIcon aria-hidden className="size-5" />}>
        <Text className="font-semibold text-orange-dark">Suspended</Text>
        <Text className="mt-0.5 text-sm">They cannot order until the account is reinstated{mayWrite ? " — use Reinstate in the header" : ""}.</Text>
      </Alert>
    );
  }
  if (status === "closed") {
    return (
      <Alert color="danger" variant="flat" bar icon={<CloseIcon aria-hidden className="size-5" />}>
        <Text className="font-semibold text-red-dark">Account closed</Text>
        <Text className="mt-0.5 text-sm">Closing is terminal — there is no reopen route.</Text>
      </Alert>
    );
  }
  return null;
}

function EmptyStateNoCustomer() {
  return <ErrorSurface error={{ title: "Not found", status: 404 }} action="load this customer" />;
}

function Figures({ excludesReceipts, mayOpenTally, children }: { excludesReceipts: boolean; mayOpenTally: boolean; children: ReactNode }) {
  const figures = <div className="grid gap-4 @lg:grid-cols-2 @6xl:grid-cols-4">{children}</div>;
  if (!excludesReceipts) return figures;

  return (
    <CaveatBanner
      caveat="The outstanding and available-credit figures below exclude every payment this customer has made. Receipts have not been applied, so they may owe considerably less than it says."
      action={
        <p>
          Receipts arrive through the Tally connector agent —{" "}
          {mayOpenTally ? (
            <Link to="/integrations/tally" className="underline">
              check whether it is running
            </Link>
          ) : (
            "check whether it is running"
          )}{" "}
          before chasing this balance.
        </p>
      }
    >
      {figures}
    </CaveatBanner>
  );
}

export type { CustomerDetail };
